/**
 * @file The select account identity component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import IdentityAvatarContainer from '../../containers/global/identity/IdentityAvatarContainer';
import IdentityNameContainer from '../../containers/global/identity/IdentityNameContainer';
import CustomControl from '../global/CustomControl';
import { UPDATE_ACCOUNT_IDENTITY_MODAL } from '../../constants/modals';
import { AVATAR_SIZE_52 } from '../../constants/avatar-sizes';
import EditIcon from '../icons/EditIcon';
import { isPpa, isPpd } from '../../util/album';

class SelectAccountIdentity extends Component {
  static propTypes = {
    identity: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      current_photo: PropTypes.object,
      has_outdated_selfie: PropTypes.bool,
    }),
    isSelectedForPurchase: PropTypes.bool,
    isSelectedForJoin: PropTypes.bool,
    isJoined: PropTypes.bool,
    isPurchased: PropTypes.bool,
    selectIdentityForPurchase: PropTypes.func.isRequired,
    deselectIdentityForPurchase: PropTypes.func.isRequired,
    selectIdentityForJoin: PropTypes.func.isRequired,
    deselectIdentityForJoin: PropTypes.func.isRequired,
    uploadedPhoto: PropTypes.object,
    openModal: PropTypes.func.isRequired,
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      display_name: PropTypes.string,
    }),
    forcePurchase: PropTypes.bool,
  };

  onEditIdentity = event => {
    event.preventDefault();
    event.stopPropagation();

    const { identity, uploadedPhoto, openModal } = this.props;

    openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
      identity,
      forceSelfieUpdate:
        (!identity.current_photo || identity.has_outdated_selfie) &&
        !uploadedPhoto,
    });
  };

  identityRequiresUpdate = identity => {
    const { uploadedPhoto, openModal } = this.props;

    if (!identity.current_photo && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        forceSelfieUpdate: true,
      });
      return true;
    }

    // `updateSelfie` request is not sync in nature, i.e.
    // the field identity.has_outdated_selfie will return `true`
    // even if we update the selfie, so to check if a user
    // really has an outdated selfie we also need to check
    // our local redux store for recent uploads for this identity,
    // if there's an `uploadedPhoto` it means that the user just
    // uploaded a selfie (which by definition is not considered outdated)
    if (identity.has_outdated_selfie && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        outdatedSelfie: true,
      });
      return true;
    }

    if (!identity.last_name) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
      });
      return true;
    }

    return false;
  };

  toggleIdentityForPurchase = event => {
    event.preventDefault();

    const {
      selectIdentityForPurchase,
      deselectIdentityForPurchase,
      isPurchased,
      isSelectedForPurchase,
      identity,
      album,
    } = this.props;

    if (this.identityRequiresUpdate(identity) || isPurchased || !isPpa(album)) {
      return;
    }

    if (isSelectedForPurchase) {
      deselectIdentityForPurchase(identity);
    } else {
      selectIdentityForPurchase(identity);
    }
  };

  toggleIdentityForJoin = event => {
    event.preventDefault();

    const {
      selectIdentityForJoin,
      deselectIdentityForJoin,
      isJoined,
      isSelectedForJoin,
      identity,
    } = this.props;

    if (this.identityRequiresUpdate(identity) || isJoined) {
      return;
    }

    if (isSelectedForJoin) {
      deselectIdentityForJoin(identity);
    } else {
      selectIdentityForJoin(identity);
    }
  };

  render() {
    const {
      identity,
      isSelectedForPurchase,
      isPurchased,
      isJoined,
      isSelectedForJoin,
      album,
      forcePurchase,
    } = this.props;

    const allowSelection =
      (!isPurchased && isPpa(album)) || (!isJoined && isPpd(album));
    let label = '';

    if (isJoined) {
      label = '(Joined)';
    }
    if (isPurchased) {
      label = '(Purchased)';
    }

    return (
      <a
        className="flex-list-item"
        href="#"
        onClick={
          isJoined || !isPpd(album) || forcePurchase
            ? this.toggleIdentityForPurchase
            : this.toggleIdentityForJoin
        }
      >
        <IdentityAvatarContainer
          identity={identity}
          className="mr-2"
          size={AVATAR_SIZE_52}
          onClick={this.onEditIdentity}
        >
          <span className="btn-identity-edit">
            <EditIcon color="#0d84fe" />
          </span>
        </IdentityAvatarContainer>

        <IdentityNameContainer className="text-dark mr-1" identity={identity} />
        {label && <span className="text-sm">{label}</span>}
        <div className="ml-auto">
          {allowSelection && (
            <CustomControl
              id={identity.id}
              type="checkbox"
              size="xl"
              checked={isSelectedForPurchase || isSelectedForJoin}
              readOnly
            />
          )}
        </div>
      </a>
    );
  }
}

export default SelectAccountIdentity;
